/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/

const DAYS_PER_MONTH = 30;
const ROUND_PRECISION = 6;

const round = (value: number): number =>
  Number(Math.round(Number(value + 'e+' + ROUND_PRECISION)) + 'e-' + ROUND_PRECISION);

type Props = Record<
  | 'cpuPricePerMonth'
  | 'memoryPricePerMonth'
  | 'storagePricePerMonth'
  | 'supportPricePerMonth'
  | 'cpuUnits'
  | 'memoryGbs'
  | 'storageGbs'
  | 'replicas',
  number
>;

type Return = Record<
  'daily' | 'monthly',
  Record<'cpu' | 'memory' | 'storage' | 'support' | 'replicas' | 'instance' | 'total', number>
>;

export const estimatePrice = (props: Props): Return => {
  const {
    cpuPricePerMonth,
    memoryPricePerMonth,
    storagePricePerMonth,
    supportPricePerMonth,
    cpuUnits,
    memoryGbs,
    storageGbs,
    replicas,
  } = props;

  const cpuMonthly = round(cpuUnits * cpuPricePerMonth);
  const memoryMonthly = round(memoryGbs * memoryPricePerMonth);
  const storageMonthly = round(storageGbs * storagePricePerMonth);

  const instanceMonthly = cpuMonthly + memoryMonthly + storageMonthly;
  const supportMonthly = round(supportPricePerMonth);

  const monthly = {
    cpu: cpuMonthly,
    memory: memoryMonthly,
    storage: storageMonthly,
    support: supportMonthly,
    instance: instanceMonthly,
    total: round(instanceMonthly * (replicas + 1) + supportMonthly),
    replicas,
  };

  return {
    daily: {
      cpu: round(monthly.cpu / DAYS_PER_MONTH),
      memory: round(monthly.memory / DAYS_PER_MONTH),
      storage: round(monthly.storage / DAYS_PER_MONTH),
      support: round(monthly.support / DAYS_PER_MONTH),
      instance: round(monthly.instance / DAYS_PER_MONTH),
      total: round(monthly.total / DAYS_PER_MONTH),
      replicas,
    },
    monthly,
  };
};
